import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTheme } from "@/components/ui/theme-provider";
import { Sun, Moon, CheckCircle2, AlertCircle, Loader2, Mail } from "lucide-react";
import logoBlack from "@assets/commentt_ai_logo_black.svg";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";

export default function VerifyEmail() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<"idle" | "verifying" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      setStatus("verifying");
      fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`)
        .then(async (res) => {
          const body = await res.json();
          if (res.ok) {
            setStatus("success");
            setMessage("Your email has been verified. Redirecting...");
            await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
            setTimeout(() => setLocation("/"), 1500);
          } else {
            setStatus("error");
            setMessage(body.message || "Verification failed.");
          }
        })
        .catch(() => {
          setStatus("error");
          setMessage("Something went wrong. Please try again.");
        });
    }
  }, []);

  const resendMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/resend-verification", {}),
    onSuccess: () => setMessage("A new verification email has been sent."),
    onError: async (err: any) => {
      const body = await err.response?.json().catch(() => ({}));
      setMessage(body?.message || "Failed to resend email.");
    },
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Link href="/">
          <img src={logoBlack} alt="Commentt.ai" className="h-7 dark:invert" />
        </Link>
        <button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-6 text-center">
          {status === "verifying" ? (
            <>
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-muted-foreground" />
              <p className="text-muted-foreground text-sm">Verifying your email...</p>
            </>
          ) : status === "success" ? (
            <>
              <CheckCircle2 className="h-10 w-10 mx-auto text-green-500" />
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">Email verified!</h1>
                <p className="text-sm text-muted-foreground">{message}</p>
              </div>
            </>
          ) : status === "error" ? (
            <>
              <AlertCircle className="h-10 w-10 mx-auto text-destructive" />
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">Verification failed</h1>
                <p className="text-sm text-muted-foreground">{message}</p>
              </div>
              <Button onClick={() => resendMutation.mutate()} variant="outline" disabled={resendMutation.isPending}>
                {resendMutation.isPending ? "Sending..." : "Resend verification email"}
              </Button>
            </>
          ) : (
            <>
              <Mail className="h-10 w-10 mx-auto text-muted-foreground" />
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
                <p className="text-sm text-muted-foreground">
                  We've sent a verification link to{" "}
                  <span className="font-medium text-foreground">{user?.email}</span>.
                  Click the link to activate your account.
                </p>
              </div>
              {message && (
                <Alert className="border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400 text-left">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
              <Button
                onClick={() => resendMutation.mutate()}
                variant="outline"
                disabled={resendMutation.isPending}
                className="w-full"
              >
                {resendMutation.isPending ? "Sending..." : "Resend verification email"}
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
